import React, { useEffect, useMemo } from "react";
import { Platform } from "react-native";
import { useTranslation } from "react-i18next";
import { Heading, FlexGrid, Text, Message } from "@clearabee/ui-library-native";
import { formatPostcode, theme } from "@clearabee/ui-library";
import { AddressField } from "components/Composite/AddressField/AddressField";
import { Input } from "components/Core/Input/Input";
import { styles } from "./DetailsForm.styles";
import { useFormContext } from "lib/hooks/useFormContext";
import { useAuth } from "lib/hooks/useAuth";
import { getValidationSchema } from "containers/ProductDetails/ProductDetails.validation";
import { z } from "zod";
import { showDeliveryText } from "lib/utils";
import { DisplayCoupon } from "components/DisplayCoupon/DisplayCoupon";
import { addressInitialValues } from "components/Composite/AddressField/helpers";

export interface DetailsFormProps {
  categorySku: string;
  disablePostcode?: boolean;
  coupon?: string;
}

export const DetailsForm = ({
  categorySku,
  disablePostcode = false,
  coupon,
}: DetailsFormProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { user } = useAuth();
  const {
    Field,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<z.infer<ReturnType<typeof getValidationSchema>>>();

  const [saveAddressToggle, deliveryAddress] = watch([
    "saveAddressToggle",
    "deliveryAddress",
  ]);

  const userIsLoggedIn = !!user;
  const userAddresses = user?.addresses || [];
  const notSkipsOrBincol = !["SKIPS", "BINCOL"].includes(categorySku);
  const showToggle =
    userIsLoggedIn && !!userAddresses.length && notSkipsOrBincol;

  /**
   * Options for the user addresses dropdown
   */
  const userAddressesOptions = useMemo(() => {
    if (!userAddresses?.length) return [];

    return userAddresses.map((address) => ({
      label: `${address.addressLine1} - ${formatPostcode(address.postcode)}`,
      value: `${address.addressLine1} - ${address.postcode}`,
    }));
  }, [JSON.stringify(userAddresses)]);

  /**
   * Check if the address has been saved
   */
  const checkIsSavedAddress = (address: typeof deliveryAddress): boolean => {
    return !!userAddresses.find(
      ({ addressLine1, postcode }) =>
        address.line1 === addressLine1 &&
        address.postcode.replaceAll(" ", "").toLowerCase() ===
          postcode.replaceAll(" ", "").toLowerCase(),
    );
  };

  const hasAddressBeenSaved =
    notSkipsOrBincol &&
    !!deliveryAddress.line1 &&
    !!deliveryAddress.postcode &&
    checkIsSavedAddress(deliveryAddress);

  /**
   * reset the save address field if the delivery address has been saved
   */
  useEffect(() => {
    if (hasAddressBeenSaved) {
      setValue("saveAddress", false);
    }
  }, [hasAddressBeenSaved]);

  /**
   * Set the save address toggle to true if the address has been saved first render
   */
  useEffect(() => {
    if (hasAddressBeenSaved) {
      setValue("saveAddressToggle", true);
    }
  }, []);

  return (
    <FlexGrid
      style={{ flexDirection: "column" }}
      rowSpacing={null}
      wrap="nowrap"
    >
      <FlexGrid.Cell style={{ zIndex: 2 }} base="100%">
        <Heading
          level={5}
          styles={styles.heading}
          color={Platform.OS === "web" ? "accent" : "brand"}
        >
          {translate(
            showDeliveryText(categorySku)
              ? "hwa.screens.productDetails.deliveryDetails"
              : "hwa.screens.productDetails.collectionDetails",
          )}
        </Heading>
        <FlexGrid rowSpacing={null}>
          <FlexGrid.Cell base="100%" large="50%">
            <Field
              name="contact.firstName"
              label={translate("common.firstName")}
              labelStyles={styles.label}
              errorStyles={styles.error}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  placeholder={translate("common.form.placeholder.firstName")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" large="50%">
            <Field
              name="contact.lastName"
              label={translate("common.lastName")}
              labelStyles={styles.label}
              errorStyles={styles.error}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  placeholder={translate("common.form.placeholder.lastName")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" large="50%">
            <Field
              name="contact.email"
              label={translate("common.email")}
              labelStyles={styles.label}
              errorStyles={styles.error}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  disabled={!!user}
                  autoCapitalize="none"
                  placeholder={translate(
                    "common.form.placeholder.emailAddress",
                  )}
                />
              )}
            </Field>
          </FlexGrid.Cell>

          <FlexGrid.Cell base="100%" large="50%">
            <Field
              name="contact.phoneNumber"
              label={translate("common.phoneNumber")}
              labelStyles={styles.label}
              errorStyles={styles.error}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <Input.InternationalPhone
                  {...field}
                  placeholder={translate("common.form.placeholder.phoneNumber")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
        </FlexGrid>
      </FlexGrid.Cell>

      {showToggle && (
        <FlexGrid.Cell
          base="100%"
          style={{ paddingBottom: theme.spacing.xsmall }}
        >
          <FlexGrid rowSpacing={null}>
            <FlexGrid.Cell base="100%">
              <Field
                name="saveAddressToggle"
                styles={{
                  marginTop: theme.spacing.small,
                  marginBottom: 0,
                }}
              >
                {({ field }) => (
                  <Input.Toggle
                    {...field}
                    label="Use Saved Address"
                    color="light"
                    labelColor="light"
                    onChange={(value) => {
                      field.onChange(value);

                      // reset saveAddress and deliveryAddress when toggling
                      clearErrors("deliveryAddress");
                      setValue("saveAddress", false);
                      setValue("deliveryAddress", {
                        ...addressInitialValues,
                        lat: null,
                        lng: null,
                      });
                    }}
                  />
                )}
              </Field>
            </FlexGrid.Cell>
          </FlexGrid>
        </FlexGrid.Cell>
      )}

      {saveAddressToggle && (
        <>
          <FlexGrid.Cell
            style={{ paddingTop: "25px", paddingBottom: "25px" }}
            base="100%"
          >
            <FlexGrid rowSpacing={null}>
              <FlexGrid.Cell base="100%">
                <Text
                  fontSize="small"
                  color="light"
                  styles={{ paddingBottom: "6px" }}
                >
                  Select a saved addresss
                </Text>

                <Field
                  name="deliveryAddress"
                  errorStyles={styles.error}
                  required
                  styles={{
                    marginTop: 0,
                    marginBottom: 1,
                  }}
                >
                  {({ field }) => (
                    <Input.List
                      {...field}
                      value={
                        !field.value
                          ? ""
                          : `${field.value.line1} - ${field.value.postcode}`
                      }
                      name="deliveryAddress"
                      options={userAddressesOptions}
                      activeStyles={{
                        backgroundColor: theme.colors.accent.base,
                      }}
                      activeIconColor="dark.base"
                      onChange={(value) => {
                        if (!value) {
                          setValue("deliveryAddress", {
                            ...addressInitialValues,
                            lat: null,
                            lng: null,
                          });
                          return;
                        }

                        const {
                          addressLine1: line1,
                          addressLine2: line2,
                          ...rest
                        } = userAddresses.find(
                          ({ addressLine1, postcode }) =>
                            value === `${addressLine1} - ${postcode}`,
                        ) || {};

                        field.onChange({
                          line1,
                          line2,
                          ...rest,
                        });
                      }}
                    />
                  )}
                </Field>
                {(errors.deliveryAddress?.line1 ||
                  errors.deliveryAddress?.postcode) && (
                  <Message
                    type="error"
                    background
                    small
                    color="light"
                    styles={[
                      styles.error,
                      {
                        backgroundColor: theme.colors.negative.base,
                        borderRadius: 4,
                        paddingHorizontal: 5,
                      },
                    ]}
                  >
                    Please select an address
                  </Message>
                )}
              </FlexGrid.Cell>
            </FlexGrid>
          </FlexGrid.Cell>
        </>
      )}

      {!saveAddressToggle && (
        <FlexGrid.Cell base="100%">
          <FlexGrid rowSpacing={null}>
            <FlexGrid.Cell base="100%">
              <AddressField
                prefix="deliveryAddress"
                labelStyles={styles.label}
                errorStyles={styles.error}
                errorBackground
                invertErrorColors
                cellSpacing={Platform.OS !== "web" ? "xsmall" : undefined}
                disablePostcode={disablePostcode}
              />
            </FlexGrid.Cell>
            {userIsLoggedIn &&
              notSkipsOrBincol &&
              !checkIsSavedAddress(deliveryAddress) && (
                <FlexGrid.Cell base="100%">
                  <Field name="saveAddress">
                    {({ field }) => (
                      <Input.Checkbox
                        {...field}
                        value={field.value}
                        inverted={Platform.OS === "web"}
                        label="Save collection address for future use"
                      />
                    )}
                  </Field>
                </FlexGrid.Cell>
              )}
          </FlexGrid>
        </FlexGrid.Cell>
      )}

      {!!coupon && (
        <FlexGrid.Cell base="100%">
          <FlexGrid
            rowSpacing={null}
            style={{
              justifyContent: "flex-end",
            }}
          >
            <FlexGrid.Cell base="100%" small="50%">
              <DisplayCoupon coupon={coupon} />
            </FlexGrid.Cell>
          </FlexGrid>
        </FlexGrid.Cell>
      )}
    </FlexGrid>
  );
};
